import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import HeaderNav from '../components/HeaderNav';
import Footer from '../components/Footer';
import DemoTweet from '../components/DemoTweet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane, 
  faComments, 
  faShieldAlt, 
  faLock, 
  faExternalLinkAlt 
} from '@fortawesome/free-solid-svg-icons';

const LandingPage = () => {
  const { 
    login, 
    authStatus, 
    showZwalletForCreation, 
    isAuthenticated, 
    ensName, 
    address 
  } = useContext(AuthContext);
  const navigate = useNavigate();

  return (
    <div className="landing-page">
      <HeaderNav isPrivate={false} />
      
      {/* Hero Section */}
      <section className="hero" style={{ textAlign: 'center', padding: '6rem 2rem' }}>
        <h1 className="gold-logo" style={{ fontSize: '5rem', marginBottom: '1rem' }}>
          Zpost
        </h1>
        <h2 style={{ fontSize: '2.5rem', color: 'var(--text-light)', marginBottom: '1.5rem' }}>
          The Future of Free Speech is Here
        </h2>
        <p style={{ 
          fontSize: '1.25rem', 
          color: 'var(--text-secondary)', 
          maxWidth: '800px', 
          margin: '0 auto 3rem',
          lineHeight: '1.6'
        }}>
          Start by creating your secure Zwallet, then claim your self-sovereign <span style={{ color: 'var(--gold)' }}>.zpost.eth</span> identity.
          No algorithms. No censorship. Just your voice, on your terms, coming soon.
        </p>
        <div className="hero-buttons" style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button 
            className="btn btn-gold" 
            onClick={() => {
              if (isAuthenticated && ensName && ensName.endsWith('.zpost.eth')) {
                navigate('/home');
              } else if (address && (!ensName || !ensName.endsWith('.zpost.eth'))) {
                alert('You need a .zpost.eth ENS domain to access Zpost.\n\nPlease mint your .zpost.eth domain first.');
                navigate('/ens');
              } else {
                showZwalletForCreation && showZwalletForCreation();
              }
            }}
            disabled={authStatus === 'connecting' || authStatus === 'creating-wallet' || authStatus === 'checking-ens'}
            style={{ 
              padding: '1.2rem 3rem', 
              fontSize: '1.2rem',
              fontWeight: 'bold'
            }}
          >
            {isAuthenticated && ensName?.endsWith('.zpost.eth') ? 'Enter Zpost →' : 
             authStatus === 'checking-ens' ? 'Verifying ENS...' : 
             authStatus === 'connecting' ? 'Connecting...' : 
             authStatus === 'creating-wallet' ? 'Creating...' : 'Get Started'}
          </button>
          
          {/* Zhat Entry Button - FIXED: Direct to /zhat/ */}
          <button 
            className="btn btn-gold" 
            onClick={() => {
              // Directly load the Zhat app from /public/zhat/ without going through main app auth
              window.location.href = '/zhat/';
            }}
            style={{ 
              padding: '1.2rem 3rem', 
              fontSize: '1.2rem',
              fontWeight: 'bold'
            }}
          >
            {isAuthenticated && ensName?.endsWith('.zpost.eth') ? '🚀 Launch Zhat Messenger' : 
             'Launch Zhat Messenger'}
          </button>
        </div>
        <p style={{ 
          color: 'var(--text-secondary)', 
          fontSize: '0.9rem', 
          marginTop: '2rem' 
        }}>
          Start with Zwallet • Claim .zpost.eth • Military-grade security
        </p>
      </section>

      {/* How It Works */}
      <section className="how-it-works" style={{ 
        padding: '5rem 2rem',
        backgroundColor: 'var(--card-bg)'
      }}>
        <div className="container">
          <h2 style={{ 
            textAlign: 'center', 
            fontSize: '2.5rem', 
            marginBottom: '3rem',
            color: 'var(--gold)'
          }}>
            How Zpost Works
          </h2>
          <div className="cards" style={{ maxWidth: '1200px', margin: '0 auto' }}>
            {/* Step 1: Create Wallet */}
            <div className="card">
              <div style={{
                fontSize: '3rem',
                color: 'var(--gold)',
                marginBottom: '1.5rem'
              }}>
                <i className="fas fa-wallet"></i>
              </div>
              <h3>1. Create Your Zwallet</h3>
              <p className="eth-label">Your Secure Vault</p>
              <p style={{ color: 'var(--text-secondary)' }}>
                Start by creating your secure embedded wallet. No external extensions needed.
                Your keys, your control. This wallet will secure your .zpost.eth domain.
              </p>
            </div>
            
            {/* Step 2: Claim .zpost.eth */}
            <div className="card">
              <div style={{
                fontSize: '3rem',
                color: 'var(--gold)',
                marginBottom: '1.5rem'
              }}>
                <i className="fas fa-id-card"></i>
              </div>
              <h3>2. Claim Your .zpost.eth</h3>
              <p className="eth-label">Your Universal Identity</p>
              <p style={{ color: 'var(--text-secondary)' }}>
                Once your wallet is ready, mint your unique username as an ENS domain.
                This becomes your permanent, self-sovereign identity across the platform.
              </p>
            </div>
            
            {/* Step 3: Start Posting */}
            <div className="card">
              <div style={{
                fontSize: '3rem',
                color: 'var(--gold)',
                marginBottom: '1.5rem'
              }}>
                <i className="fas fa-comments"></i>
              </div>
              <h3>3. Start Posting Freely</h3>
              <p className="eth-label">Decentralized Social</p>
              <p style={{ color: 'var(--text-secondary)' }}>
                Join conversations, build communities, and express yourself without
                algorithmic manipulation or centralized control. Your voice, your rules.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Profile Control Hero Section - NEW SECTION */}
      <section className="profile-hero" style={{ 
        padding: '5rem 2rem',
        background: 'linear-gradient(135deg, var(--card-bg) 0%, rgba(255,215,0,0.05) 100%)',
        borderTop: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)'
      }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ 
              fontSize: '2.8rem', 
              marginBottom: '1.5rem',
              color: 'var(--gold)'
            }}>
              Your Digital Identity, Your Control
            </h2>
            <p style={{ 
              fontSize: '1.3rem', 
              color: 'var(--text-light)', 
              marginBottom: '2rem',
              lineHeight: '1.7'
            }}>
              Your avatar, bio, and social connections are your sovereign digital identity on Zpost.
            </p>
            <div style={{ 
              background: 'rgba(255, 215, 0, 0.05)',
              padding: '2.5rem',
              borderRadius: '15px',
              border: '1px solid var(--gold-shadow)',
              marginBottom: '2rem'
            }}>
              <p style={{ 
                fontSize: '1.25rem', 
                color: 'var(--text-secondary)', 
                lineHeight: '1.8',
                fontStyle: 'italic',
                margin: 0
              }}>
                "Your Avatar, Bio and more is the data used in your profile in Zpost. 
                <span style={{ color: 'var(--gold)', fontWeight: 'bold' }}> You control your profile, not Us</span>"
              </p>
            </div>
            <p style={{ 
              fontSize: '1.1rem', 
              color: 'var(--text-secondary)', 
              maxWidth: '800px',
              margin: '0 auto'
            }}>
              Your .zpost.eth ENS domain is your universal Web3 identity. Unlike traditional platforms where your data is locked in and monetized by the company, on Zpost your profile is owned by you, stored on the blockchain, and portable across applications.
            </p>
          </div>
        </div>
      </section>

      {/* Demo Tweet Section */}
      <section className="demo-section" style={{ 
        padding: '5rem 2rem',
        background: 'linear-gradient(to bottom, var(--card-bg) 0%, var(--black) 100%)',
        borderBottom: '1px solid var(--border)'
      }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{ 
              fontSize: '2.8rem', 
              marginBottom: '1rem',
              color: 'var(--gold)',
              textShadow: '0 2px 10px var(--gold-shadow)'
            }}>
              See Zpost in Action
            </h2>
            <p style={{ 
              fontSize: '1.2rem', 
              color: 'var(--text-secondary)', 
              maxWidth: '700px', 
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              This is a <span style={{ color: 'var(--gold)', fontWeight: 'bold' }}>real example</span> of what you'll create on Zpost.
              Your .zpost.eth domain becomes your verified identity for free speech.
            </p>
          </div>
          
          <DemoTweet />
          
          <div style={{ 
            textAlign: 'center', 
            marginTop: '3rem',
            padding: '2rem',
            background: 'rgba(255, 215, 0, 0.05)',
            borderRadius: '15px',
            border: '1px solid var(--gold-shadow)'
          }}>
            <h3 style={{ 
              color: 'var(--gold)', 
              marginBottom: '1rem',
              fontSize: '1.8rem'
            }}>
              Your Turn to Speak
            </h3>
            <p style={{ 
              color: 'var(--text-secondary)', 
              marginBottom: '2rem',
              fontSize: '1.1rem'
            }}>
              Start by creating your Zwallet, then claim your .zpost.eth domain and join the conversation.
            </p>
            <div className="hero-buttons">
              <button 
                className="btn btn-gold" 
                onClick={() => {
                  if (isAuthenticated && ensName && ensName.endsWith('.zpost.eth')) {
                    navigate('/home');
                  } else if (address && (!ensName || !ensName.endsWith('.zpost.eth'))) {
                    alert('You need a .zpost.eth ENS domain to access Zpost.\n\nPlease mint your .zpost.eth domain first.');
                    navigate('/ens');
                  } else {
                    showZwalletForCreation && showZwalletForCreation();
                  }
                }}
                disabled={authStatus === 'connecting' || authStatus === 'creating-wallet' || authStatus === 'checking-ens'}
                style={{ 
                  padding: '1.2rem 3rem', 
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 15px var(--gold-shadow)'
                }}
              >
                {isAuthenticated && ensName?.endsWith('.zpost.eth') ? 'Enter Zpost →' : 
                 authStatus === 'checking-ens' ? 'Verifying ENS...' : 
                 authStatus === 'connecting' ? 'Connecting...' : 
                 authStatus === 'creating-wallet' ? 'Creating...' : 'Create Zwallet & Start →'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ZHAT MESSENGER SECTION - INTEGRATED ABOVE "Why Choose Zpost?" */}
      <section id="zhat" className="zhat-section" style={{ 
        padding: '4rem 2rem', 
        background: 'linear-gradient(135deg, var(--card-bg) 0%, rgba(128,0,0,0.1) 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto 4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div className="gold-logo" style={{ fontSize: '3.5rem' }}>Zhat</div>
              <span style={{ 
                color: 'var(--gold)', 
                fontWeight: 'bold', 
                background: 'rgba(128,0,0,0.3)', 
                padding: '0.5rem 1.5rem', 
                borderRadius: '20px', 
                fontSize: '1rem',
                border: '1px solid var(--gold)',
                boxShadow: '0 4px 12px rgba(255,215,0,0.2)'
              }}>
                Secure Messenger
              </span>
            </div>
            <h2 style={{ fontSize: '2.8rem', color: 'var(--text-light)', marginBottom: '1.5rem', lineHeight: '1.2' }}>
              Decentralized ENS-to-ENS Messaging
            </h2>
            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', lineHeight: '1.6', maxWidth: '700px', margin: '0 auto 2rem' }}>
              Chat securely with any ENS domain holder. No servers, no middlemen, just peer-to-peer encrypted messages.
              Built on Web3 principles for complete privacy and ownership of your conversations.
            </p>
          </div>

          {/* Three Column Layout: Image | Text | Image */}
          <div className="zhat-showcase" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '3rem', 
            maxWidth: '1400px', 
            margin: '0 auto',
            flexWrap: 'wrap'
          }}>
            {/* Left Image - john1.png */}
            <div style={{ flex: 1, minWidth: '300px', textAlign: 'center' }}>
              <div style={{ 
                background: 'linear-gradient(135deg, var(--card-bg) 0%, rgba(0,0,0,0.5) 100%)', 
                border: '3px solid var(--gold)', 
                borderRadius: '15px', 
                padding: '1rem', 
                boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 30px rgba(255,215,0,0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Golden frame effect */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  border: '1px solid rgba(255,215,0,0.3)',
                  borderRadius: '12px',
                  pointerEvents: 'none',
                  zIndex: 2
                }}></div>
                
                {/* Image Container - EXACTLY LIKE MyUrl SECTION */}
                <div style={{ 
                  width: '500px', 
                  maxWidth: '100%',
                  height: '375px',
                  maxHeight: '375px',
                  borderRadius: '10px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto', 
                  overflow: 'hidden', 
                  position: 'relative',
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  border: '2px solid var(--gold)',
                  boxShadow: '0 8px 24px rgba(255,215,0,0.2)'
                }}>
                  {/* John Connor Image - EXACT SAME STYLE AS MyUrl SECTION */}
                  <img 
                    src="/john1.png" 
                    alt="John Connor Zhat Interface" 
                    style={{ 
                      width: '500px',
                      height: '375px',
                      objectFit: 'contain', // CRITICAL: Same as MyUrl section
                      borderRadius: '10px',
                    }} 
                  />
                </div>
                
                {/* Image caption */}
                <div style={{ 
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: 'rgba(0,0,0,0.7)',
                  borderRadius: '8px',
                  border: '1px solid var(--border)'
                }}>
                  <h4 style={{ color: 'var(--gold)', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                    <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '0.5rem' }} />
                    Send Messages
                  </h4>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                    Instant encrypted messaging to any ENS domain
                  </p>
                </div>
              </div>
            </div>

            {/* Center Text Content */}
            <div style={{ flex: 1.2, minWidth: '300px', textAlign: 'center' }}>
              <div style={{ 
                background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, var(--card-bg) 100%)', 
                border: '2px solid var(--gold)', 
                borderRadius: '20px', 
                padding: '3rem 2.5rem', 
                boxShadow: '0 25px 70px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,215,0,0.1)',
                height: '100%'
              }}>
                <div style={{ marginBottom: '2.5rem' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    margin: '0 auto 1.5rem',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--gold) 0%, #DAA520 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2.5rem',
                    color: 'var(--black)',
                    boxShadow: '0 8px 32px rgba(255,215,0,0.4)'
                  }}>
                    <FontAwesomeIcon icon={faComments} />
                  </div>
                  <h3 style={{ 
                    fontSize: '2.2rem', 
                    color: 'var(--text-light)', 
                    marginBottom: '1.5rem',
                    fontWeight: 'bold'
                  }}>
                    Web3 Native Messaging
                  </h3>
                </div>

                {/* Feature Grid */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '1.5rem', 
                  marginBottom: '2.5rem'
                }}>
                  <div style={{ 
                    padding: '1.5rem',
                    background: 'rgba(255,215,0,0.05)',
                    borderRadius: '12px',
                    border: '1px solid var(--border)',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      margin: '0 auto 1rem',
                      borderRadius: '50%',
                      background: 'rgba(255,215,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      color: 'var(--gold)'
                    }}>
                      <FontAwesomeIcon icon={faShieldAlt} />
                    </div>
                    <h4 style={{ color: 'var(--gold)', marginBottom: '0.5rem', fontSize: '1rem' }}>
                      End-to-End Encrypted
                    </h4>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
                      Messages secured by Web3 cryptography
                    </p>
                  </div>

                  <div style={{ 
                    padding: '1.5rem',
                    background: 'rgba(255,215,0,0.05)',
                    borderRadius: '12px',
                    border: '1px solid var(--border)',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      margin: '0 auto 1rem',
                      borderRadius: '50%',
                      background: 'rgba(255,215,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      color: 'var(--gold)'
                    }}>
                      <FontAwesomeIcon icon={faLock} />
                    </div>
                    <h4 style={{ color: 'var(--gold)', marginBottom: '0.5rem', fontSize: '1rem' }}>
                      No Central Servers
                    </h4>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
                      Direct ENS-to-ENS communication
                    </p>
                  </div>
                </div>

                {/* Call to Action */}
                <div style={{ marginTop: 'auto' }}>
                  <div style={{ 
                    background: 'rgba(128,0,0,0.2)', 
                    padding: '1.5rem', 
                    borderRadius: '12px',
                    border: '1px solid var(--dark-red)',
                    marginBottom: '1.5rem'
                  }}>
                    <p style={{ 
                      color: 'var(--text-light)', 
                      margin: 0,
                      fontSize: '1rem',
                      fontFamily: 'monospace'
                    }}>
                      <span style={{ color: 'var(--gold)' }}>john.zpost.eth</span> → <span style={{ color: 'var(--gold)' }}>mary.zpost.eth</span>
                    </p>
                    <p style={{ 
                      color: 'var(--text-secondary)', 
                      margin: '0.5rem 0 0 0',
                      fontSize: '0.9rem',
                      fontStyle: 'italic'
                    }}>
                      This is how decentralized messaging works
                    </p>
                  </div>
                  
                  {/* Zhat Button - FIXED: Direct to /zhat/ */}
                  <button 
                    className="btn btn-gold" 
                    onClick={() => {
                      // Directly load the Zhat app from /public/zhat/ without going through main app auth
                      window.location.href = '/zhat/';
                    }}
                    disabled={authStatus === 'connecting' || authStatus === 'creating-wallet' || authStatus === 'checking-ens'}
                    style={{ 
                      padding: '1rem 3rem', 
                      fontSize: '1.1rem', 
                      fontWeight: 'bold',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.75rem',
                      width: '100%'
                    }}
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                    {isAuthenticated && ensName?.endsWith('.zpost.eth') 
                      ? `Launch Zhat as ${ensName}` 
                      : 'Launch Zhat Messenger'}
                  </button>
                </div>
              </div>
            </div>

            {/* Right Image - mary1.png */}
            <div style={{ flex: 1, minWidth: '300px', textAlign: 'center' }}>
              <div style={{ 
                background: 'linear-gradient(135deg, var(--card-bg) 0%, rgba(0,0,0,0.5) 100%)', 
                border: '3px solid var(--gold)', 
                borderRadius: '15px', 
                padding: '1rem', 
                boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 30px rgba(255,215,0,0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Golden frame effect */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  border: '1px solid rgba(255,215,0,0.3)',
                  borderRadius: '12px',
                  pointerEvents: 'none',
                  zIndex: 2
                }}></div>
                
                {/* Image Container - EXACTLY LIKE MyUrl SECTION */}
                <div style={{ 
                  width: '500px', 
                  maxWidth: '100%',
                  height: '375px',
                  maxHeight: '375px',
                  borderRadius: '10px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto', 
                  overflow: 'hidden', 
                  position: 'relative',
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  border: '2px solid var(--gold)',
                  boxShadow: '0 8px 24px rgba(255,215,0,0.2)'
                }}>
                  {/* Mary Magdalene Image - EXACT SAME STYLE AS MyUrl SECTION */}
                  <img 
                    src="/mary1.png" 
                    alt="Mary Magdalene Zhat Interface" 
                    style={{ 
                      width: '500px',
                      height: '375px',
                      objectFit: 'contain', // CRITICAL: Same as MyUrl section
                      borderRadius: '10px',
                    }} 
                  />
                </div>
                
                {/* Image caption */}
                <div style={{ 
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: 'rgba(0,0,0,0.7)',
                  borderRadius: '8px',
                  border: '1px solid var(--border)'
                }}>
                  <h4 style={{ color: 'var(--gold)', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                    <FontAwesomeIcon icon={faComments} style={{ marginRight: '0.5rem' }} />
                    ENS Contacts
                  </h4>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                    Chat with any .eth domain holder directly
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Features */}
          <div style={{ 
            marginTop: '4rem',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem'
          }}>
            <div style={{ 
              textAlign: 'center',
              padding: '2rem',
              background: 'rgba(0,0,0,0.4)',
              borderRadius: '12px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ color: 'var(--gold)', fontSize: '2rem', marginBottom: '1rem' }}>
                <FontAwesomeIcon icon={faShieldAlt} />
              </div>
              <h4 style={{ color: 'var(--text-light)', marginBottom: '0.5rem' }}>Zero Knowledge</h4>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                Even we can't read your messages
              </p>
            </div>

            <div style={{ 
              textAlign: 'center',
              padding: '2rem',
              background: 'rgba(0,0,0,0.4)',
              borderRadius: '12px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ color: 'var(--gold)', fontSize: '2rem', marginBottom: '1rem' }}>
                <FontAwesomeIcon icon={faPaperPlane} />
              </div>
              <h4 style={{ color: 'var(--text-light)', marginBottom: '0.5rem' }}>Instant Delivery</h4>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                Real-time Web3 messaging protocol
              </p>
            </div>

            <div style={{ 
              textAlign: 'center',
              padding: '2rem',
              background: 'rgba(0,0,0,0.4)',
              borderRadius: '12px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ color: 'var(--gold)', fontSize: '2rem', marginBottom: '1rem' }}>
                <FontAwesomeIcon icon={faLock} />
              </div>
              <h4 style={{ color: 'var(--text-light)', marginBottom: '0.5rem' }}>Registration</h4>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                Your .Zhat.eth domain is your identity
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features" style={{ padding: '5rem 2rem' }}>
        <div className="container">
          <h2 style={{ 
            textAlign: 'center', 
            fontSize: '2.5rem', 
            marginBottom: '3rem',
            color: 'var(--gold)'
          }}>
            Why Choose Zpost?
          </h2>
          
          <div className="cards" style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {[
              {
                icon: 'fas fa-shield-alt',
                title: 'Censorship-Resistant',
                desc: 'Your content lives on decentralized infrastructure. No single entity can remove your voice.'
              },
              {
                icon: 'fas fa-user-lock',
                title: 'True Ownership',
                desc: 'Your .zpost.eth ENS domain is your property. Transfer it, sell it, or take it elsewhere.'
              },
              {
                icon: 'fas fa-link',
                title: 'Your Profile on Blockchain',
                desc: 'Your avatar, bio, and social connections live as ENS records on Ethereum. You own your digital identity with blockchain-grade security and portability across Web3.'
              },
              {
                icon: 'fas fa-heart',
                title: 'Free Speech',
                desc: 'Free Speech. Your voice, your rules – just keep it civilized. Zpost and Zhat are where free expression meets mutual respect'
              },
              {
                icon: 'fas fa-code',
                title: 'Open Protocol',
                desc: 'Built on open standards. Other apps can interact with the Zpost social graph.'
              },
              {
                icon: 'fas fa-infinity',
                title: 'Permanent Identity',
                desc: 'Your .zpost.eth identity lasts as long as Ethereum exists. No deplatforming.'
              }
            ].map((feature, index) => (
              <div key={index} className="card" style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2.5rem',
                  color: 'var(--gold)',
                  marginBottom: '1rem'
                }}>
                  <i className={feature.icon}></i>
                </div>
                <h3>{feature.title}</h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="comparison" style={{ 
        padding: '5rem 2rem',
        backgroundColor: 'var(--card-bg)'
      }}>
        <div className="container">
          <h2 style={{ 
            textAlign: 'center', 
            fontSize: '2.5rem', 
            marginBottom: '3rem',
            color: 'var(--gold)'
          }}>
            Zpost vs Traditional Social
          </h2>
          
          <div style={{ 
            maxWidth: '900px', 
            margin: '0 auto',
            overflowX: 'auto'
          }}>
            <table style={{ 
              width: '100%',
              borderCollapse: 'collapse',
              color: 'var(--text-light)'
            }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--gold)' }}>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Feature</th>
                  <th style={{ padding: '1rem', textAlign: 'center', color: 'var(--gold)' }}>Zpost</th>
                  <th style={{ padding: '1rem', textAlign: 'center' }}>Traditional Platforms</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['First Step', 'Create Zwallet', 'Sign up with email'],
                  ['Identity', 'You own it (.zpost.eth ENS)', 'Platform owns it'],
                  ['Profile Data', 'On Ethereum via ENS, you control', 'Platform controls & monetizes'],
                  ['Content Control', 'You control it', 'Platform can remove it'],
                  ['Monetization', 'Coming soon', 'Platform sells your data'],
                  ['Portability', 'Take your identity anywhere', 'Locked to platform'],
                  ['Cost', 'One-time domain mint ($50 est.)', '"Free" (you are the product)']
                ].map((row, index) => (
                  <tr key={index} style={{ 
                    borderBottom: '1px solid var(--border)',
                    backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'
                  }}>
                    <td style={{ padding: '1rem', fontWeight: 'bold' }}>{row[0]}</td>
                    <td style={{ 
                      padding: '1rem', 
                      textAlign: 'center',
                      color: 'var(--gold)',
                      fontWeight: 'bold'
                    }}>{row[1]}</td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta" style={{ 
        padding: '6rem 2rem',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 style={{ 
            fontSize: '3rem', 
            marginBottom: '1.5rem',
            color: 'var(--gold)'
          }}>
            Ready to Own Your Voice?
          </h2>
          <p style={{ 
            fontSize: '1.25rem', 
            color: 'var(--text-secondary)', 
            maxWidth: '700px', 
            margin: '0 auto 3rem',
            lineHeight: '1.6'
          }}>
            Join the first social network where you truly own your identity, your content,
            and your relationships. Start with Zwallet, then claim your .zpost.eth ENS domain.
          </p>
          <div className="hero-buttons" style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              className="btn btn-gold" 
              onClick={() => {
                if (isAuthenticated && ensName && ensName.endsWith('.zpost.eth')) {
                  navigate('/home');
                } else if (address && (!ensName || !ensName.endsWith('.zpost.eth'))) {
                  alert('You need a .zpost.eth ENS domain to access Zpost.\n\nPlease mint your .zpost.eth domain first.');
                  navigate('/ens');
                } else {
                  showZwalletForCreation && showZwalletForCreation();
                }
              }}
              disabled={authStatus === 'connecting' || authStatus === 'creating-wallet' || authStatus === 'checking-ens'}
              style={{ 
                padding: '1.2rem 3rem', 
                fontSize: '1.2rem',
                fontWeight: 'bold'
              }}
            >
              {isAuthenticated && ensName?.endsWith('.zpost.eth') ? 'Enter Zpost →' : 
               authStatus === 'checking-ens' ? 'Verifying ENS...' : 
               authStatus === 'connecting' ? 'Connecting...' : 
               authStatus === 'creating-wallet' ? 'Creating...' : 'Create Zwallet Now →'}
            </button>
            
            {/* Zhat Button in Final CTA - FIXED: Direct to /zhat/ */}
            <button 
              className="btn btn-gold" 
              onClick={() => {
                // Directly load the Zhat app from /public/zhat/ without going through main app auth
                window.location.href = '/zhat/';
              }}
              style={{ 
                padding: '1.2rem 3rem', 
                fontSize: '1.2rem',
                fontWeight: 'bold'
              }}
            >
              {isAuthenticated && ensName?.endsWith('.zpost.eth') ? '🚀 Launch Zhat Messenger' : 
               'Launch Zhat Messenger'}
            </button>
          </div>
          <p style={{ 
            color: 'var(--text-secondary)', 
            fontSize: '0.9rem', 
            marginTop: '2rem' 
          }}>
            Already have a .zpost.eth? <button 
              onClick={() => login && login({ mode: 'connect' })}
              style={{ 
                background: 'none',
                border: 'none',
                color: 'var(--gold)',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Click here to access the app
            </button>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
